/**
 * Provider for sharing approve/revert state from page-level companion actions
 * up to the CompanionOverlay in app-layout.
 */

import { useState, useCallback, useMemo, type ReactNode } from 'react'
import type { ActionSnapshot } from './types'
import { CompanionActionContext, type ActionProposal } from './companion-action-hooks'

export function CompanionActionProvider({ children }: { children: ReactNode }) {
  const [pendingAction, setPendingAction] = useState<ActionSnapshot | null>(null)
  const [pendingProposal, setPendingProposal] = useState<ActionProposal | null>(null)
  const [handlers, setHandlers] = useState<{
    approve: (() => void) | null
    revert: (() => void) | null
  }>({ approve: null, revert: null })

  const setActionHandlers = useCallback((approve: () => void, revert: () => void) => {
    setHandlers({ approve, revert })
  }, [])

  const clearActionHandlers = useCallback(() => {
    setHandlers({ approve: null, revert: null })
    setPendingAction(null)
    setPendingProposal(null)
  }, [])

  // Memoize so this provider doesn't broadcast a fresh reference identity to
  // every consumer on every parent re-render. Without this, AppLayout's
  // alert subscription (useAlertEffects) cascades through the layout subtree
  // and recreates the context value on every alert dispatch — cheap consumers
  // re-render needlessly, expensive ones (CompanionOverlay) can do worse.
  const value = useMemo(
    () => ({
      pendingAction,
      setPendingAction,
      pendingProposal,
      setPendingProposal,
      approveAction: handlers.approve,
      revertAction: handlers.revert,
      setActionHandlers,
      clearActionHandlers,
    }),
    [pendingAction, pendingProposal, handlers, setActionHandlers, clearActionHandlers],
  )

  return <CompanionActionContext.Provider value={value}>{children}</CompanionActionContext.Provider>
}
